import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { selectMechanicDefaultBranchId } from "../../../redux/auth";
import { createVoiceOrder } from "../../../services/order";
import { uploadAudioFile, uploadImageFile } from "../../../services/uploadFile";
import {
  formatTime,
  convertToBase64,
  isImageFile,
} from "../../../helpers/global";
import { RECORDING_STATUS } from "../../../constants/voiceRecord";
import RecordButton from "./RecordButton/RecordButton";
import AudioPlayer from "../../AudioPlayer/AudioPlayer";
import Loading from "../../UI/Loading/Loading";
import PrimaryButton from "../../UI/PrimaryButton/PrimaryButton";
import DeleteButton from "../../UI/DeleteButton/DeleteButton";
import BranchSelectionModal from "../../UI/Modals/BranchSelectionModal/BranchSelectionModal";
import OrderSuccessModal from "../../UI/Modals/OrderSuccessModal/OrderSuccessModal";
import ErrorMessage from "../../UI/Error/ErrorMessage/ErrorMessage";
import cameraIcon from "../../../assets/icons/camera.svg";
import classes from "./VoiceOrderForm.module.css";

const VoiceOrderForm = () => {
  const defaultBranchId = useSelector(selectMechanicDefaultBranchId);
  const [isRecordSupported, setIsRecordSupported] = useState(true);
  const [isRecordPermissionGranted, setIsRecordPermissionGranted] =
    useState(true);
  const [recordingStatus, setRecordingStatus] = useState(
    RECORDING_STATUS.INACTIVE
  );
  const [mediaStream, setMediaStream] = useState();
  const [audioChunks, setAudioChunks] = useState([]);
  const [recordElapsedTime, setRecordElapsedTime] = useState(0); // in seconds
  const [audioSource, setAudioSource] = useState();
  const [audioSourceLoading, setAudioSourceLoading] = useState(false);
  const [audioSourceLoadingError, setAudioSourceLoadingError] = useState("");
  const [isImageUploadLoading, setIsImageUploadLoading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState("");
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isSubmitWithoutRecording, setIsSubmitWithoutRecording] =
    useState(false);
  const [isUploadImagesWithoutRecording, setIsUploadImagesWithoutRecording] =
    useState(false);
  const [isBranchSelectModalVisible, setIsBranchSelectModalVisible] =
    useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState();
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [isSubmitOrderLoading, setIsSubmitOrderLoading] = useState(false);
  const [submitOrderError, setSubmitOrderError] = useState("");
  const mediaRecorder = useRef();
  const fileInputRef = useRef();

  const recordVoiceClickedHandler = async () => {
    if ("MediaRecorder" in window) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        setMediaStream(stream);
      } catch (error) {
        setIsRecordPermissionGranted(false);
      }
    } else {
      setIsRecordSupported(false);
    }
  };

  const resetRecorder = () => {
    mediaRecorder.current.stream
      .getAudioTracks()
      .forEach((track) => track.stop());
    mediaRecorder.current = null;
    setRecordElapsedTime(0);
  };

  const fetchAudioSource = async () => {
    const blob = new Blob(audioChunks, { type: "audio/ogg; codecs=opus" });
    const recordBase64 = await convertToBase64(blob);
    setAudioSourceLoading(true);
    setAudioSourceLoadingError("");
    resetRecorder();
    try {
      const response = await uploadAudioFile(recordBase64);
      const {
        data: { response: audioSource },
      } = response;
      setAudioSource(audioSource);
    } catch (error) {
      console.error(error);
      setAudioSourceLoadingError("حدث خطأ أثناء تحميل الملف الصوتي");
    } finally {
      setAudioSourceLoading(false);
    }
  };

  const pauseRecordClickedHandler = () => {
    setRecordingStatus(RECORDING_STATUS.INACTIVE);
    mediaRecorder.current.stop();
    mediaRecorder.current.onstop = fetchAudioSource;
  };

  const audioSourceDeletedHandler = () => {
    setAudioSource(null);
  };

  const uploadImageClickedHandler = () => {
    if (!audioSource) {
      setIsUploadImagesWithoutRecording(true);
      return;
    }

    fileInputRef.current.click();
  };

  const fileSelectedHandler = async (event) => {
    const {
      target: { files },
    } = event;
    const selectedFile = files[0];
    if (!selectedFile) {
      return;
    } else if (!isImageFile(selectedFile)) {
      setImageUploadError("من فضلك اختر ملفات صور فقط");
      return;
    } else {
      const imageBase64 = await convertToBase64(selectedFile);
      setIsImageUploadLoading(true);
      setImageUploadError("");
      try {
        const response = await uploadImageFile(imageBase64);
        const {
          data: { response: imageUrl },
        } = response;
        setUploadedImages((prevUploadedImages) => [
          ...prevUploadedImages,
          { imageUrl },
        ]);
      } catch (error) {
        setImageUploadError("حدث خطأ ما اثناء تحميل الصورة");
      } finally {
        setIsImageUploadLoading(false);
      }
    }
    // reset the input value to be able to re-add the same image again
    event.target.value = "";
  };

  const imageDeletedHandler = (index) => {
    setUploadedImages((prevImages) =>
      prevImages.filter((_, idx) => idx !== index)
    );
  };

  const checkFormValidity = () => {
    let isValid = true;
    if (!audioSource) {
      setIsSubmitWithoutRecording(true);
      isValid = false;
    }
    return isValid;
  };

  const voiceOrderSubmitHandler = async (event) => {
    event.preventDefault();
    const isValid = checkFormValidity();
    if (isValid) {
      setIsBranchSelectModalVisible(true);
    }
  };

  const branchSelectModalClosedHandler = () => {
    setIsBranchSelectModalVisible(false);
  };

  const branchChangedHandler = (event) => {
    setSelectedBranchId(Number(event.target.value));
  };

  const createVoiceOrderConfirmedHandler = async () => {
    const payload = {
      audioUrl: audioSource,
      imageUrlList: uploadedImages,
      branchId: selectedBranchId,
    };

    setIsSubmitOrderLoading(true);
    setSubmitOrderError("");
    try {
      await createVoiceOrder(payload);
      branchSelectModalClosedHandler();
      setIsSuccessModalVisible(true);
    } catch (error) {
      setSubmitOrderError("حدث خطأ ما أثناء تأكيد الطلب");
    } finally {
      setIsSubmitOrderLoading(false);
    }
  };

  const orderSuccessModalClosedHandler = () => {
    setAudioSource("");
    setUploadedImages([]);
    setIsSuccessModalVisible(false);
  };

  useEffect(() => {
    if (mediaStream) {
      setIsSubmitWithoutRecording(false);
      setIsUploadImagesWithoutRecording(false);
      setRecordingStatus(RECORDING_STATUS.RECORDING);
      mediaRecorder.current = new MediaRecorder(mediaStream);
      mediaRecorder.current.start();
      const audioChunks = [];
      mediaRecorder.current.ondataavailable = (event) => {
        const { data } = event;
        if (typeof data === "undefined") return;
        else if (data.size === 0) return;
        else audioChunks.push(data);
      };
      setAudioChunks(audioChunks);
    }
  }, [mediaStream]);

  useEffect(() => {
    let recordTimeIntervalId = null;
    if (recordingStatus === RECORDING_STATUS.RECORDING) {
      recordTimeIntervalId = setInterval(() => {
        setRecordElapsedTime((prevElapsedTime) => prevElapsedTime + 1);
      }, 1000);
    } else {
      if (recordTimeIntervalId) {
        clearInterval(recordTimeIntervalId);
      }
    }

    return () => {
      if (recordTimeIntervalId) {
        clearInterval(recordTimeIntervalId);
      }
    };
  }, [recordingStatus]);

  useEffect(() => {
    return () => {
      if (mediaRecorder.current) {
        resetRecorder();
      }
    };
  }, []);

  useEffect(() => {
    setSelectedBranchId(defaultBranchId);
  }, [defaultBranchId]);

  return (
    <form
      method="POST"
      autoComplete="off"
      onSubmit={voiceOrderSubmitHandler}
      className={classes["voice-order-form"]}
    >
      <div className={classes["voice-order-form__inputs-wrapper"]}>
        <div className={classes["voice-order-form__record-wrapper"]}>
          {!audioSourceLoadingError && (
            <p
              className={classNames(
                classes["voice-order-form__record-status-text"],
                {
                  [classes["voice-order-form__record-status-text--error"]]:
                    !isRecordSupported || !isRecordPermissionGranted,
                }
              )}
            >
              {audioSource
                ? "تم التسجيل"
                : audioSourceLoading
                ? "جارٍ تجهيز التسجيل"
                : !isRecordSupported
                ? "هذا المتصفح لا يدعم تسجيل الصوت"
                : !isRecordPermissionGranted
                ? "من فضلك اسمح بإتصال الميكروفون لكي تتمكن من تسجيل الطلب"
                : recordingStatus === RECORDING_STATUS.INACTIVE
                ? "دوس هنا عشان تبدأ تسجيل طلبك"
                : "جارٍ التسجيل"}
            </p>
          )}
          {audioSource ? (
            <div
              className={classes["voice-order-form__audio-controls-wrapper"]}
            >
              <DeleteButton
                onClick={audioSourceDeletedHandler}
                className={classes["voice-order-form__delete-audio-btn"]}
              />
              <AudioPlayer
                src={audioSource}
                className={classes["voice-order-form__audio-player"]}
              />
            </div>
          ) : audioSourceLoading ? (
            <Loading className={classes["voice-order-form__audio-loading"]} />
          ) : audioSourceLoadingError ? (
            <ErrorMessage message={audioSourceLoadingError} />
          ) : (
            <RecordButton
              recordingStatus={recordingStatus}
              error={isSubmitWithoutRecording || isUploadImagesWithoutRecording}
              onClick={
                recordingStatus === RECORDING_STATUS.INACTIVE
                  ? recordVoiceClickedHandler
                  : pauseRecordClickedHandler
              }
              type="button"
            />
          )}
          {recordingStatus === RECORDING_STATUS.RECORDING && (
            <p
              className={classNames(
                classes["voice-order-form__elapsed-time"],
                "numeric-content"
              )}
            >
              {formatTime(recordElapsedTime)}
            </p>
          )}
        </div>
        <div className={classes["voice-order-form__images-wrapper"]}>
          <input
            type="file"
            accept="image/*"
            onChange={fileSelectedHandler}
            className="hidden"
            ref={fileInputRef}
          />
          {uploadedImages.length > 0 ? (
            <>
              <div className={classes["voice-order-form__line"]} />
              <div
                className={classes["voice-order-form__uploaded-images-wrapper"]}
              >
                {uploadedImages.map((image, index) => (
                  <div
                    key={index}
                    className={
                      classes["voice-order-form__uploaded-image-wrapper"]
                    }
                  >
                    <img
                      src={image.imageUrl}
                      alt="uploaded sku"
                      className={classes["voice-order-form__uploaded-image"]}
                    />
                    <DeleteButton
                      type="button"
                      onClick={() => imageDeletedHandler(index)}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={uploadImageClickedHandler}
                  disabled={isImageUploadLoading}
                  className={
                    classes["voice-order-form__upload-another-image-btn"]
                  }
                >
                  اضافة صورة
                  <img
                    src={cameraIcon}
                    alt="upload"
                    className={
                      classes["voice-order-form__upload-another-image-img"]
                    }
                  />
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={uploadImageClickedHandler}
              className={classes["voice-order-form__upload-image-btn"]}
              disabled={isImageUploadLoading}
            >
              <div className={classes["voice-order-form__camera-wrapper"]}>
                <img
                  src={cameraIcon}
                  alt="upload images"
                  className={classes["voice-order-form__camera-icon"]}
                />
              </div>
              <div>
                <p className={classes["voice-order-form__upload-image-text"]}>
                  اضافة صورة لتوضيح القطعة
                </p>
                <p className={classes["voice-order-form__upload-image-rules"]}>
                  (إختياري)
                </p>
              </div>
            </button>
          )}
          {isImageUploadLoading ? (
            <Loading className={classes["voice-order-form__image-loading"]} />
          ) : imageUploadError || isUploadImagesWithoutRecording ? (
            <div className={classes["voice-order-form__upload-image-error"]}>
              <ErrorMessage
                message={
                  isUploadImagesWithoutRecording
                    ? "قم بتسجيل الطلب الصوتي أولا"
                    : imageUploadError
                }
              />
            </div>
          ) : null}
        </div>
      </div>

      <div className={classes["voice-order-form__submit-wrapper"]}>
        <PrimaryButton
          withAnimation
          type="submit"
          disabled={
            recordingStatus === RECORDING_STATUS.RECORDING ||
            audioSourceLoading ||
            isImageUploadLoading ||
            isSubmitOrderLoading
          }
          className={classes["voice-order-form__submit-btn"]}
        >
          إرسال الطلب الصوتي
        </PrimaryButton>
        {isSubmitWithoutRecording && (
          <ErrorMessage
            message="قم بتسجيل الطلب الصوتي"
            className={classes["voice-order-form__submit-error"]}
          />
        )}
      </div>

      <BranchSelectionModal
        isOpen={isBranchSelectModalVisible}
        onClose={branchSelectModalClosedHandler}
        currentSelectedBranchId={selectedBranchId}
        onChangeBranch={branchChangedHandler}
        footer={
          <div>
            <PrimaryButton
              withAnimation
              disabled={isSubmitOrderLoading}
              onClick={createVoiceOrderConfirmedHandler}
              className={classes["voice-order-form__confirm-btn"]}
            >
              تأكيد الطلب
            </PrimaryButton>
            {submitOrderError && (
              <div
                className={classes["voice-order-form__confirm-error-wrapper"]}
              >
                <ErrorMessage
                  message={submitOrderError}
                  className={classes["voice-order-form__confirm-error"]}
                />
              </div>
            )}
            {isSubmitOrderLoading && (
              <Loading
                className={classes["voice-order-form__confirm-loading"]}
              />
            )}
          </div>
        }
      />
      <OrderSuccessModal
        isOpen={isSuccessModalVisible}
        onClose={orderSuccessModalClosedHandler}
        message="تم إرسال الطلب الصوتي بنجاح"
      />
    </form>
  );
};

export default VoiceOrderForm;
